import { Fragment } from 'react';
import type { FC } from 'react';

import type {
  SvpCategoryAdditional,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';
import type {
  SvpAssetAdditional,
  SvpAssetId,
} from '@vgtv/api-client/lib/svp_asset';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FavoriteFilled as FavoriteFilledIcon,
  FavoriteOutlined as FavoriteOutlinedIcon,
} from '@vgno/icons';

import { List } from '../../List/List';
import { getCategoryMainImage } from '../../../shared/helpers/categoryHelpers';
import { getImageSources } from '../../../helpers/images';
import { EmptyState } from '../EmptyState/EmptyState';
import { Placeholder, placeholderTypes } from '../../Placeholder/Placeholder';
import { usePlaceholders } from '../../PageComponents/usePlaceholders';
import { isSvpAsset, isSvpCategory } from '../../../modules/helpers';
import { AssetThumbnail } from '../../Asset/AssetThumbnail';
import { getAssetMainImage } from '../../../shared/helpers/assetHelpers';
import { getPath, routeNames } from '../../../shared/helpers/routes';
import { useVendor } from '../../VendorPovider/VendorPovider';
import { mapItemToType } from '../../../helpers/favourites';

import * as styles from './FavouritesListHorizontal.module.scss';

interface FavouritesListHorizontalProps {
  favourites: {
    item: SvpCategoryAdditional | SvpAssetAdditional;
    isFilled: boolean;
  }[];
  isLoading: boolean;
  handleClick: (
    id: SvpCategoryId | SvpAssetId,
    type: 'asset' | 'category'
  ) => void;
}

const FavouritesListHorizontal: FC<FavouritesListHorizontalProps> = ({
  favourites,
  isLoading,
  handleClick,
}) => {
  const { t } = useTranslation();

  const placeholders = usePlaceholders(!isLoading, 5);
  const { vendor } = useVendor();

  if (isLoading) {
    return (
      <>
        <div className={styles.loadingHeader}></div>
        <List>
          {placeholders.map((item, index) => (
            <Placeholder key={index} type={placeholderTypes.favourites} />
          ))}
        </List>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>{t('My list')}</div>
      {favourites.length > 0 ? (
        <List>
          {favourites.map(({ item, isFilled }) => {
            const imageUrl = isSvpCategory(item)
              ? getCategoryMainImage(item)
              : getAssetMainImage(item);

            const imageSources = imageUrl
              ? getImageSources({
                  imageUrl,
                  imagePreset: 'categoriesList',
                })
              : undefined;

            const icon = isFilled ? (
              <FavoriteFilledIcon />
            ) : (
              <FavoriteOutlinedIcon />
            );

            let targetUrl: string | undefined;
            if (item) {
              targetUrl = isSvpAsset(item)
                ? getPath(routeNames.asset, {
                    asset: item,
                  })
                : getPath(routeNames.category, {
                    category: item,
                    vendor,
                  });
            }

            return (
              <Fragment key={item.id}>
                {targetUrl && (
                  <Link to={targetUrl}>
                    <AssetThumbnail imageSources={imageSources} />
                  </Link>
                )}
                <div className={styles.title}>
                  {item.title}
                  <button
                    onClick={() => handleClick(item.id, mapItemToType(item))}
                  >
                    {icon}
                  </button>
                </div>
              </Fragment>
            );
          })}
        </List>
      ) : (
        <EmptyState />
      )}
    </>
  );
};

export { FavouritesListHorizontal };
